import pluralize from 'pluralize';
import { type ChangeEvent, memo, useCallback, useMemo, useState } from 'react';

import { useTrackCanvasMediaImported } from '@air/analytics';
import { type Clip, ClipType } from '@air/api/types';
import { commands, useCommandHistory } from '@air/feature-edit-canvas';
import { Button } from '@air/primitive-button';
import { Input } from '@air/primitive-input';
import { Modal, ModalCloseButton, ModalTitle } from '@air/primitive-modal';
import { useToasts } from '@air/provider-toast';
import { convertUnknownToError, reportErrorToBugsnag } from '@air/utils-error';
import { cn } from '@air/utils-tailwind';

import { clipsToAppNodes } from '~/components/Modals/AddToCanvasModal/clipsToAppNodes';
import { DEFAULT_DIMENSION, NODE_SPACING } from '~/components/Modals/AddToCanvasModal/clipToAppNode';
import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';
import { useSelectedCanvasId } from '~/hooks/canvases/useSelectedCanvasId';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { type CanvasListItem } from '~/swr-hooks/canvases/types';
import { getCanvasDetailKey } from '~/swr-hooks/canvases/useCanvas';
import { useListCanvases } from '~/swr-hooks/canvases/useListCanvases';
import { usePrivateCanvasesCreateNodesMutation } from '~/swr-hooks/canvases/usePrivateCanvasesCreateNodesMutation';
import { queryClient } from '~/swr-hooks/queryClient';

export type AddToCanvasModalProps = {
  clips: Clip[];
};

export const AddToCanvasModal = memo(({ clips, onClose }: AirModalProps<AddToCanvasModalProps>) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { showToast } = useToasts();
  const { mutateAsync: createNodes, isPending } = usePrivateCanvasesCreateNodesMutation();
  const { selectCanvas } = useSelectedCanvasId();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCanvasId, setSelectedCanvasId] = useState<string | null>(null);
  const { trackCanvasMediaImported } = useTrackCanvasMediaImported();
  const execute = useCommandHistory((state) => state.execute);

  const { data: canvases, isLoading } = useListCanvases({
    workspaceId: currentWorkspace?.id,
    view: 'mine',
  });

  const filteredCanvases = useMemo(() => {
    if (!canvases) {
      return [];
    }
    if (!searchQuery) {
      return canvases;
    }
    const lowerQuery = searchQuery.toLowerCase();

    return canvases.filter((canvas: CanvasListItem) => canvas.title?.toLowerCase().includes(lowerQuery));
  }, [canvases, searchQuery]);

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  }, []);

  const handleCanvasSelect = useCallback((canvasId: string) => {
    setSelectedCanvasId((prev) => (prev === canvasId ? null : canvasId));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!selectedCanvasId || !currentWorkspace?.id) {
      return;
    }

    try {
      const rightPanelState = useWorkspaceRightPanelStore.getState();
      const isTargetCanvasOpen = rightPanelState.open && rightPanelState.selectedCanvasId === selectedCanvasId;

      if (isTargetCanvasOpen) {
        // Canvas is already open — add directly to the live store so the nodes appear immediately.
        // addNodes also triggers server sync via the injected createNodes callback.
        execute(commands.addNodes(clipsToAppNodes(clips), { zoomToFit: false, zoomToFitIfFirstNode: true }));
      } else {
        // Canvas is not open — persist via API first, then remove the cached canvas detail
        // so the fresh fetch triggered by selectCanvas isn't beaten by stale cached data.
        // (useCanvasForRightPanel only syncs nodes once per canvas open; if stale cache is
        // used for that first sync, the new nodes would be silently dropped.)
        let offsetX = 0;
        await createNodes({
          workspaceId: currentWorkspace.id,
          canvasId: selectedCanvasId,
          nodes: clips.map((clip) => {
            const x = offsetX;
            offsetX += (clip.width || DEFAULT_DIMENSION) + NODE_SPACING;
            return {
              name: clip.title || clip.importedName || '',
              position: { x, y: 0 },
              width: clip.width || DEFAULT_DIMENSION,
              height: clip.height || DEFAULT_DIMENSION,
              zIndex: '1',
              data: {
                type: clip.type === ClipType.video ? 'video' : 'image',
                fileName: clip.title || clip.importedName || '',
                media: {
                  image: clip.assets?.image,
                  original: clip.assets?.original,
                  ...(clip.type === ClipType.video && { video: clip.assets?.original }),
                },
                source: 'workspace',
                assetId: clip.assetId,
                versionId: clip.id,
              },
            };
          }),
        });

        queryClient.removeQueries({
          queryKey: getCanvasDetailKey({ canvasId: selectedCanvasId, workspaceId: currentWorkspace.id }),
        });
        selectCanvas(selectedCanvasId);
      }

      trackCanvasMediaImported({
        canvas_id: selectedCanvasId,
        number_of_media: clips.length,
        source: 'workspace',
      });

      const label = clips.length === 1 ? 'Asset added to canvas' : `${clips.length} assets added to canvas`;
      showToast(label, { color: 'black' });
      onClose();
    } catch (error) {
      showToast('Failed to add assets to canvas', { color: 'red' });
      reportErrorToBugsnag({
        error: convertUnknownToError(error),
        context: 'Failed to add assets to canvas',
      });
    }
  }, [
    selectedCanvasId,
    currentWorkspace?.id,
    createNodes,
    selectCanvas,
    clips,
    showToast,
    onClose,
    trackCanvasMediaImported,
    execute,
  ]);

  return (
    <Modal isOpen onDismiss={onClose}>
      <div className="flex flex-col gap-4">
        <header className="flex justify-between gap-4">
          <ModalTitle>Add to canvas...</ModalTitle>
          <ModalCloseButton className="shrink-0" onClick={onClose} />
        </header>
        <p className="text-14 text-grey-10">
          {`The ${pluralize('asset', clips.length, clips.length > 1)}`} will be added to a canvas and will not be
          removed from their current location.
        </p>
        <Input
          onChange={handleSearchChange}
          placeholder={`Search ${currentWorkspace?.name ?? 'canvases'}`}
          value={searchQuery}
        />
        <div className="overflow-hidden rounded-8 border border-grey-5 p-3">
          <p className="p-2 font-medium text-14 text-grey-11">My canvases</p>
          <div className="max-h-60 overflow-y-auto">
            {isLoading && <p className="py-2 text-14 text-grey-10">Loading canvases...</p>}
            {!isLoading && filteredCanvases.length === 0 && (
              <p className="py-2 text-14 text-grey-10">No canvases found</p>
            )}
            {filteredCanvases.map((canvas: CanvasListItem) => (
              <button
                className={cn(
                  'w-full truncate py-2 pl-6 text-left text-14 text-grey-12',
                  selectedCanvasId === canvas.id ? 'bg-blue-3' : 'hover:bg-grey-3',
                )}
                key={canvas.id}
                onClick={() => handleCanvasSelect(canvas.id)}
                type="button"
              >
                {canvas.title}
              </button>
            ))}
          </div>
        </div>
        <footer className="flex justify-end gap-3">
          <Button appearance="ghost" color="grey" onClick={onClose} size="large">
            Cancel
          </Button>
          <Button
            appearance="filled"
            color="blue"
            disabled={!selectedCanvasId || isPending}
            isLoading={isPending}
            onClick={handleSubmit}
            size="large"
          >
            {isPending ? 'Adding...' : 'Add to canvas'}
          </Button>
        </footer>
      </div>
    </Modal>
  );
});

AddToCanvasModal.displayName = 'AddToCanvasModal';
